import { createSlice } from "@reduxjs/toolkit";
import { initialProjectList } from "@utils/builderUtils";
import { Actions } from "@typedefs/interfaces";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { TopbarStep } from "@typedefs/interfaces";
import type { RootState } from "@app/store";
import type { BIntactMapState, Builder, MainView, ResultsTab } from "./builderTypes";

const initialState: Builder = {
  tabsOpen: true,
  mainView: "tier1",
  resultsTab: "ghg",
  activeAction: Actions.LiveUpdates,
  actionsList: [],
  moduleList: [],
  projectList: initialProjectList,
  isFetchingData: false,
  currentModuleThreads: [],
  activeThread: null,
  selectedLanguage: 'en',
  bIntactMapState: null,
  compareProjectId: "",
}

export const BuilderSlice = createSlice({
  name: "builder",
  initialState,
  reducers: {
    setBuilder: (state, action: PayloadAction<Builder>) => {
      const { projectList, tabsOpen } = action.payload;
      state.projectList = projectList
      state.tabsOpen = tabsOpen
    },
    toggleTabsOpen: (state, action: PayloadAction<undefined | boolean>) => { state.tabsOpen = action.payload !== undefined ? action.payload : !state.tabsOpen },
    setMainView: (state, action: PayloadAction<MainView>) => { state.mainView = action.payload },
    setModuleList: (state, action: PayloadAction<TopbarStep[]>) => { state.moduleList = action.payload },
    setActionList: (state, action: PayloadAction<Actions[]>) => { state.actionsList = action.payload },
    setActiveAction: (state, action: PayloadAction<Actions>) => { state.activeAction = action.payload },
    setIsFetchingData: (state, action: PayloadAction<boolean>) => { state.isFetchingData = action.payload },
    setCurrentModuleThreads: (state, action: PayloadAction<number[]>) => { state.currentModuleThreads = action.payload },
    setActiveThread: (state, action: PayloadAction<number | null>) => { state.activeThread = action.payload },
    setSelectedLanguage: (state, action: PayloadAction<string>) => { state.selectedLanguage = action.payload },
    setBIntactMapState: (state, action: PayloadAction<BIntactMapState | null>) => { state.bIntactMapState = action.payload },
    setResultsTab: (state, action: PayloadAction<ResultsTab>) => { state.resultsTab = action.payload },
    setCompareProjectId: (state, action: PayloadAction<string>) => { state.compareProjectId = action.payload },
  }
})

export const {
  setBuilder,
  setActiveAction,
  setResultsTab,
  setCompareProjectId,
  toggleTabsOpen,
  setMainView,
  setModuleList,
  setIsFetchingData,
  setActionList,
  setActiveThread,
  setCurrentModuleThreads,
  setSelectedLanguage,
  setBIntactMapState,
} = BuilderSlice.actions

export const selectCurrentBuilder = (state: RootState) => state.builder;
// With the actions rail collapsed the main column is wide enough for both tiers
// side by side; with it open they share the column and the tab picks one.
// Tier 1 is the default view: show it unless Tier 2 is explicitly selected.
// (`!== "tier2"` rather than `=== "tier1"` so a persisted state from before
// `mainView` existed — rehydrated as undefined — still shows the module form.)
export const selectShowTier1 = (state: RootState) => !state.builder.tabsOpen || state.builder.mainView !== "tier2";
export const selectShowTier2 = (state: RootState) => !state.builder.tabsOpen || state.builder.mainView === "tier2";
export const selectCurrentActions = (state: RootState) => state.builder.actionsList;
export const selectIsFetchingData = (state: RootState) => state.builder.isFetchingData;
export const selectActiveThread = (state: RootState) => state.builder.activeThread;
export const selectCurrentModuleThreads = (state: RootState) => state.builder.currentModuleThreads;
export const selectSelectedLanguage = (state: RootState) => state.builder.selectedLanguage;
export const selectBIntactMapState = (state: RootState) => state.builder.bIntactMapState;

export default BuilderSlice.reducer; 